import * as Sentry from "@sentry/browser"
import algoliasearch from "algoliasearch"
import type { Config } from "@ironzion/frontend-js-config"

type GlobalAlgolia = {
  apiKey: string
  searchIdCounter: number
  indexCache: {
    [key: string]: algoliasearch.Index
  }
  searchCache: algoliasearch.Client
  useCases: { [key: string]: string }
}

declare global {
  interface Window {
    // TODO: pass this around instead of putting it in window
    algolia?: GlobalAlgolia
  }
}

/**
 * Initialize Algolia for the whole window. Should only be called once after
 * getting the search API token after logging in.
 */
export function initializeAlgoliaWindow(config: Config, searchApiToken: string): void {
  if (window.algolia) {
    return
  }

  // attach agolia api key to window for webcomponents to use
  window.algolia = {
    apiKey: searchApiToken,
    searchIdCounter: 0,
    indexCache: {},
    searchCache: algoliasearch(config.algolia.appId, searchApiToken),
    useCases: {},
  }

  Object.entries(config.algolia.indexes).map(([useCase, index]) => initializeIndex(index, useCase))
}

/** Add an index+usecase to algolia */
export function initializeIndex(index: string, useCase: string) {
  if (!window.algolia) {
    throw "window.algolia was not initialized. Have you called `initializeAlgoliaWindow`?"
  }

  if (!index) {
    const message = "Algolia Search is missing a key component when someone is attempting to search. index is missing"
    Sentry.captureException(new Error(message))
    return console.error(message)
  }

  if (!window.algolia.indexCache[index]) {
    window.algolia.indexCache[index] = window.algolia.searchCache.initIndex(index)
  }

  // at this time the default is always used. Even global search is using the default index.
  window.algolia.useCases[useCase] = index
}
